/******/ (() => { // webpackBootstrap
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    // DOM elements
    this.input = this.querySelector('[data-predictive-search-input]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');
    this.clearButton = this.querySelector('[data-predictive-search-clear]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.emptyStateContainer = this.querySelector('[data-predictive-search-empty-state]');
    this.status = this.querySelector('[data-predictive-search-status]');
    this.boxShadowElement = this.closest('.popup-shadow');

    // Initial state
    this.isPredictive = this.getAttribute('is-predictive') === 'true';
    this.searchQuery = this.input.value;
    this.searchChangeTimer = null;
    this.abortController = null;
  
    // Bind event handlers
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClearButtonVisibility = this.handleClearButtonVisibility.bind(this);
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.handleNavButtonKeyDown = this.handleNavButtonKeyDown.bind(this);
    this.resetState = this.resetState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-predictive-search'});

    // Add event listeners
    this.input.addEventListener('input', this.handleInputChange);
    this.input.addEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.addEventListener('click', this.handleSubmit);
    this.clearButton.addEventListener('click', this.handleClear);
  }

  handleInputChange() {
    this.handleClearButtonVisibility();

    if (!this.isPredictive) return;

    const query = this.input.value.trim();

    if (query !== '') {
      this.searchQuery = query;

      if (this.searchChangeTimer) {
        window.clearTimeout(this.searchChangeTimer);
      }

      this.searchChangeTimer = window.setTimeout(() => {
        this.performSearch(this.searchQuery);
        this.searchChangeTimer = null;
      }, 250);
    }
    else {
      this.resetState();
    }
  }

  handleClearButtonVisibility() {
    if (this.input.value === '') {
      this.clearButton.classList.add('fade-out');
      setTimeout(() => {
        this.clearButton.classList.add('hide');
        this.clearButton.classList.remove('fade-out');
      }, 250);
    }
    else {
      this.clearButton.classList.remove('hide');
    }
  }

  handleInputKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleSubmit() {
    window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(this.input.value)}*`;
  }

  handleClear() {
    this.input.value = '';
    this.searchQuery = '';
    this.handleClearButtonVisibility();
    this.input.focus();
    this.resetState();
  }

  async performSearch(query) {
    const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search-results&resources[limit]=10&resources[limit_scope]=each&resources[type]=query,product,collection,page,article`;
    if (this.abortController) {
      this.abortController.abort();
    }

    const requestController = new AbortController();
    this.abortController = requestController;
    this.resultsContainer?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: requestController.signal });
      const responseText = await response.text();

      if (response.status === 417) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Predictive search request failed with status ${response.status}`);
      }

      if (this.input.value.trim() !== query) return;

      if (this.emptyStateContainer) {
        this.emptyStateContainer.setAttribute('hidden', '');
      }

      // Render the results
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const content = doc.querySelector('.shopify-section');
      if (content) {
        this.renderSearchResults(content.innerHTML);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(`Failed to fetch search results:`, error);
    } finally {
      if (this.abortController === requestController) {
        this.resultsContainer?.setAttribute('aria-busy', 'false');
        this.abortController = null;
      }
    }
  }

  renderSearchResults(html) {
    this.resultsContainer.innerHTML = html;

    // Remove any previously tracked event listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
        el.removeEventListener('keydown', this.handleNavButtonKeyDown);
      });
    }

    // Track elements with event listeners for later cleanup
    this.boundNavButtons = [];

    // Add nav button event listeners
    const navButtons = this.resultsContainer.querySelector('[data-predictive-search-nav-buttons]');
    if (navButtons) {
      navButtons.addEventListener('click', this.handleNavButtonClick);
      navButtons.addEventListener('keydown', this.handleNavButtonKeyDown);
      this.boundNavButtons.push(navButtons);
    }

    const announcement = this.resultsContainer
      .querySelector('[data-predictive-search-announcement]')
      ?.getAttribute('data-predictive-search-announcement');
    this.announce(announcement);
  }

  handleNavButtonClick(e) {
    const navButton = e.target.closest('[role="tab"]');
    if (!navButton) return;
    e.stopPropagation();
    this.activateNavButton(navButton);
  }

  handleNavButtonKeyDown(e) {
    const navButton = e.target.closest('[role="tab"]');
    if (!navButton) return;

    const navButtons = Array.from(e.currentTarget.querySelectorAll('[role="tab"]'));
    const currentIndex = navButtons.indexOf(navButton);
    let nextIndex = currentIndex;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % navButtons.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + navButtons.length) % navButtons.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = navButtons.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    navButtons[nextIndex].focus();
    this.activateNavButton(navButtons[nextIndex]);
  }

  activateNavButton(navButton) {
    const resultId = navButton.getAttribute('data-nav-id');
    if (!resultId) return;

    const results = this.resultsContainer.querySelector(`[data-grid-id="${resultId}"]`);
    if (!results) return;

    const previousActiveButton = this.resultsContainer.querySelector(`[data-nav-id].active`);
    const previousActiveResult = this.resultsContainer.querySelector(`[data-grid-id]:not([hidden])`);

    // Set active nav button
    if (previousActiveButton) {
      previousActiveButton.setAttribute('aria-selected', 'false');
      previousActiveButton.setAttribute('tabindex', '-1');
      previousActiveButton.classList.remove('active');
    }
    navButton.setAttribute('aria-selected', 'true');
    navButton.setAttribute('tabindex', '0');
    navButton.classList.add('active');

    // Set active results
    const heightContainer = this.resultsContainer.querySelector('[data-predictive-search-main-results]');
    const beforeHeight = heightContainer.clientHeight;

    if (previousActiveResult) {
      previousActiveResult.setAttribute('hidden', '');
    }
  
    results.removeAttribute('hidden');

    // Transition the height change
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      heightContainer.animate([
        { height: `${beforeHeight}px` },
        { height: `${heightContainer.clientHeight}px` }
      ], { duration: 500, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' });
    }
  }

  announce(message) {
    if (!this.status) return;
    this.status.textContent = '';
    if (!message) return;
    window.requestAnimationFrame(() => {
      this.status.textContent = message;
    });
  }

  resetState() {
    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.resultsContainer?.setAttribute('aria-busy', 'false');
    this.announce('');

    if (this.emptyStateContainer) {
      this.emptyStateContainer.removeAttribute('hidden');
      this.renderSearchResults('');
      return;
    }

    this.resultsContainer.classList.add('fade-out');
    if (this.boxShadowElement) {
      this.boxShadowElement.classList.remove('popup-shadow');
    }

    setTimeout(() => {
      this.renderSearchResults('');
      this.resultsContainer.classList.remove('fade-out');
      if (this.boxShadowElement) {
        this.boxShadowElement.classList.add('popup-shadow');
      }
    }, 300);
  }

  disconnectedCallback() {
    // Remove event listeners
    this.input.removeEventListener('input', this.handleInputChange);
    this.input.removeEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.removeEventListener('click', this.handleSubmit);
    this.clearButton.removeEventListener('click', this.handleClear);

    // Clean up dynamic listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
        el.removeEventListener('keydown', this.handleNavButtonKeyDown);
      });
    }

    if (this.abortController) {
      this.abortController.abort();
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

/******/ })()
;
